import React from 'react'

import * as c from '../../../../common'
import * as cp from '../../../comps'

import { getSummaryNotes, ICost } from './Cost'

interface NotesPageProps {
  costs: readonly ICost[]
  customerData: c.Customer
  noPDF?: boolean
  /**
   * Signing v2 (13213613154, "Signature on the notes too"): one customer
   * signature at the end of the notes. Kept in the same block as the last
   * note so it never lands alone on a page (V47).
   */
  signature?: React.ReactNode
}

const groupStyle: React.CSSProperties = {
  alignSelf: 'flex-start',
  display: 'flow-root',
  width: '100%',
}
const headingStyle: React.CSSProperties = {
  borderBottom: '1px solid rgba(0, 0, 0, 0.15)',
  color: '#202124',
  fontSize: 17,
  fontWeight: 700,
  margin: '0 0 7px',
  paddingBottom: 5,
  textAlign: 'left',
}
const headingStyleAfter: React.CSSProperties = {
  ...headingStyle,
  marginTop: 22,
}
const lineStyle: React.CSSProperties = {
  color: '#202124',
  fontFamily: 'var(--ups-font-family)',
  fontSize: 13,
  lineHeight: 1.4,
  margin: '0 0 4px',
  overflowWrap: 'anywhere',
  textAlign: 'left',
  whiteSpace: 'pre-wrap',
}
// Product title (`2 x Door Sensors`).
const productLineStyle: React.CSSProperties = {
  ...lineStyle,
  color: '#1a6e3a',
  fontWeight: 700,
}
// A person wrote this, the app did not: bold (Jaron, Slack 2026-09-11).
const customLineStyle: React.CSSProperties = {
  ...lineStyle,
  fontWeight: 700,
}
const signatureStyle: React.CSSProperties = {
  borderTop: '1px solid rgba(0, 0, 0, 0.15)',
  marginTop: 14,
  paddingTop: 10,
}
const separatorStyle: React.CSSProperties = {
  borderTop: '1px solid rgba(0, 0, 0, 0.12)',
  height: 1,
  margin: '7px 0',
}

const categoryLabel = (customer: c.Customer, field: c.CustomerField): string =>
  field.startsWith('custom_')
    ? (customer[(field + '_name') as keyof c.Customer] as string | undefined) ||
      ''
    : field === 'security_system'
    ? 'Smart Home'
    : c.getFieldLabel(field)

/**
 * One block that must not be split across pages, plus the divider that
 * separates it from the block before it. The first block of a category also
 * carries the category heading so a heading is never stranded alone.
 */
interface NoteBlock {
  readonly key: string
  readonly nodes: readonly React.ReactNode[]
}

const customLines = (text: string, keyPrefix: string): React.ReactNode[] =>
  text
    .split('\n')
    .filter((line) => line.trim().length > 0)
    .map((line, i) => (
      <p key={`${keyPrefix}-${i}`} style={customLineStyle}>
        {line}
      </p>
    ))

export default React.memo<NotesPageProps>(function NotesPage({
  costs,
  customerData,
  noPDF,
  signature,
}) {
  const content: React.ReactNode[] = []

  const pushSection = (
    key: string,
    heading: string,
    blocks: readonly NoteBlock[],
  ): void => {
    // A category heading that follows another section needs air above it, or
    // it reads as part of the previous product (Luis QA 2026-09-29).
    const hasPrev = content.length > 0
    blocks.forEach((block, i) => {
      content.push(
        <div key={block.key} style={groupStyle}>
          {i > 0 && <div key={`${block.key}-sep`} style={separatorStyle} />}
          {i === 0 && (
            <h2
              key={`${key}-heading`}
              style={hasPrev ? headingStyleAfter : headingStyle}
            >
              {heading}
            </h2>
          )}
          {block.nodes}
        </div>,
      )
    })
  }

  costs.forEach(({ name }) => {
    const field = name as c.CustomerField
    const { auto, custom } = getSummaryNotes(customerData, field)
    const blocks: NoteBlock[] = []

    // Each product: its title line, then everything else as ONE paragraph -
    // the contract Scope of Work style Jaron marked as fine (12790297504 /
    // 13028234765), instead of a row per bullet.
    c.toProductNotes(auto, field.startsWith('security_')).forEach(
      ({ body, header }, i) => {
        blocks.push({
          key: `${field}-auto-${i}`,
          nodes: [
            <p key="h" style={productLineStyle}>
              {header}
            </p>,
            ...(body
              ? [
                  <p key="b" style={lineStyle}>
                    {body}
                  </p>,
                ]
              : []),
          ],
        })
      },
    )
    if (custom.trim()) {
      const isAirConditionerCustom =
        field.startsWith('custom_') &&
        categoryLabel(customerData, field) === 'Air Conditioner'
      const customProductNotes = isAirConditionerCustom
        ? c.toProductNotes(custom, false)
        : []
      blocks.push({
        key: `${field}-custom`,
        nodes: isAirConditionerCustom
          ? customProductNotes.flatMap(({ body, header }, i) => [
              <p key={`h-${i}`} style={productLineStyle}>
                {header}
              </p>,
              ...(body
                ? [
                    <p key={`b-${i}`} style={customLineStyle}>
                      {body}
                    </p>,
                  ]
                : []),
            ])
          : customLines(custom, `${field}-custom`),
      })
    }
    if (blocks.length)
      pushSection(field, categoryLabel(customerData, field), blocks)
  })

  const globalNotes = (customerData.globalNotes || '').trim()
  if (globalNotes) {
    // `globalNotes` is typed by the rep, except for the one line the store
    // injects for two-story homes - that one is the app's, so it stays regular.
    const nodes = globalNotes
      .split('\n')
      .filter((line) => line.trim().length > 0)
      .map((line, i) => (
        <p
          key={`global-${i}`}
          style={
            line.trim() === c.TWO_STORY_FEE_NOTE ? lineStyle : customLineStyle
          }
        >
          {line}
        </p>
      ))
    pushSection('global', 'General Notes', [{ key: 'global-notes', nodes }])
  }

  if (!content.length) return null
  if (signature) {
    const last = content.pop()
    content.push(
      <div key="notes-signature" style={groupStyle}>
        {last}
        <div style={signatureStyle}>{signature}</div>
      </div>,
    )
  }
  return (
    <cp.ReportPages
      companyBorder
      hPad
      noPDF={noPDF}
      state={customerData.solarCompany}
      title="NOTES"
    >
      {content}
    </cp.ReportPages>
  )
})
